import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import { mixingBatchService } from '../services/api';
import PageBackHeader from '../components/PageBackHeader';

function formatDt(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('uk-UA', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function normalizeList(res) {
  const d = res.data;
  return Array.isArray(d?.results) ? d.results : Array.isArray(d) ? d : [];
}

/** Номер замовлення для людини: з замовлення клієнта (як у планері); інакше «—». */
function displayOrderRef(row) {
  const label = (row?.order_number_display || '').trim();
  if (label) return label;
  return '—';
}

const EPS = 1e-6;

/**
 * Якщо в «Додати» введено більше, ніж залишилось до номіналу рецепту — масштаб f = (додано+введено)/потрібно;
 * показуємо, скільки кожного компонента треба мати загалом (ціль) і скільки ще додати.
 */
function computeRecipeScaleHints(allLines, addQtyMap) {
  if (!allLines?.length) return null;
  let fMax = 0;
  for (const ln of allLines) {
    const raw = String(addQtyMap[ln.id] ?? '').trim().replace(',', '.');
    const x = parseFloat(raw);
    if (!Number.isFinite(x) || x <= EPS) continue;
    const req = Number(ln.quantity_required) || 0;
    const added = Number(ln.quantity_added) || 0;
    const rem = Number(ln.quantity_remaining);
    const remaining = Number.isFinite(rem) ? rem : Math.max(0, req - added);
    if (req < EPS) continue;
    if (x <= remaining + 1e-5) continue;
    const f = (added + x) / req;
    if (f > fMax) fMax = f;
  }
  if (fMax <= 1 + 1e-8) return null;

  const rows = allLines.map((ln) => {
    const req = Number(ln.quantity_required) || 0;
    const added = Number(ln.quantity_added) || 0;
    const target = fMax * req;
    const need = Math.max(0, target - added);
    return {
      id: ln.id,
      label: ln.material_label,
      need,
      target,
      needDisp: Math.round(need * 10000) / 10000,
      targetDisp: Math.round(target * 10000) / 10000,
    };
  });
  return { f: Math.round(fMax * 100000) / 100000, rows };
}

export default function MixingLinePage({ currentUser }) {
  const [tab, setTab] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [addQty, setAddQty] = useState({});
  const [savingLine, setSavingLine] = useState(null);
  /** Знімок підказки після OK — лишається до закриття діалогу (якщо знову вводять перевищення, показується актуальна підказка). */
  const [persistedRecipeHints, setPersistedRecipeHints] = useState(null);

  const canAccess =
    currentUser?.effective_permissions &&
    currentUser.effective_permissions['menu.production.mixing'] !== false;

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      const params = tab === 0 ? {} : { history: 1 };
      const res = await mixingBatchService.list(params);
      setRows(normalizeList(res));
    } catch (e) {
      console.error(e);
      alert('Не вдалося завантажити задачі змішування');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const openDetail = async (id) => {
    try {
      setDetailLoading(true);
      setPersistedRecipeHints(null);
      const res = await mixingBatchService.getById(id);
      setDetail(res.data);
      setAddQty({});
    } catch (e) {
      console.error(e);
      alert('Не вдалося завантажити деталі');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetail(null);
    setAddQty({});
    setPersistedRecipeHints(null);
  };

  const recipeScaleHints = useMemo(
    () => computeRecipeScaleHints(detail?.lines || [], addQty),
    [detail, addQty],
  );

  const displayRecipeHints = useMemo(
    () => recipeScaleHints || persistedRecipeHints,
    [recipeScaleHints, persistedRecipeHints],
  );

  const submitAdd = async (lineId) => {
    if (!detail) return;
    const raw = String(addQty[lineId] ?? '').trim().replace(',', '.');
    const q = parseFloat(raw);
    if (!Number.isFinite(q) || q <= 0) {
      alert('Вкажіть додатну кількість (кг)');
      return;
    }
    try {
      setSavingLine(lineId);
      const hintsSnapshot = computeRecipeScaleHints(detail.lines || [], addQty);
      if (hintsSnapshot) {
        setPersistedRecipeHints(JSON.parse(JSON.stringify(hintsSnapshot)));
      }
      const res = await mixingBatchService.recordAdd(detail.id, { line_id: lineId, quantity: q });
      setDetail(res.data);
      setAddQty((prev) => ({ ...prev, [lineId]: '' }));
      await loadRows();
    } catch (e) {
      const d = e.response?.data;
      const first = (v) => (Array.isArray(v) ? v[0] : v);
      const msg =
        first(d?.quantity) ||
        first(d?.detail) ||
        first(d?.line_id) ||
        (typeof d === 'object' && d
          ? first(Object.values(d).flat?.() || Object.values(d))
          : null) ||
        e.message;
      alert(msg || 'Помилка збереження');
    } finally {
      setSavingLine(null);
    }
  };

  if (!canAccess) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Немає доступу до «Лінії змішування» (право menu.production.mixing).</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <PageBackHeader to="/production" label="До виробництва" />
      <Typography variant="h4" gutterBottom>
        Лінія змішування сировини
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 900 }}>
        Задачі створюються з планера при додаванні замовлення з матеріалами (техкарта та перемол). Дедлайн
        «звести суміш до» враховує час сушіння: плановий старт на ТПА мінус максимальний час сушіння серед
        рядків із позначкою «Потрібне сушіння». Доступність рахується зі складу виробництва; якщо там не
        вистачає компонента, показано частку суміші, яку можна змішати зараз. Задача лишається відкритою,
        доки не буде додано повні норми по всіх компонентах.
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Активні задачі" />
        <Tab label="Історія (завершені)" />
      </Tabs>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Typography color="text.secondary">
          {tab === 0 ? 'Немає відкритих задач змішування.' : 'Поки що немає завершених задач.'}
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Звести суміш до</TableCell>
                <TableCell>Замовлення (номер)</TableCell>
                <TableCell>Виріб</TableCell>
                <TableCell>Станок</TableCell>
                <TableCell align="right">К-ть (шт)</TableCell>
                <TableCell>Старт на ТПА (план)</TableCell>
                <TableCell>Прогрес</TableCell>
                <TableCell align="right">Частка за складом</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow
                  key={r.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => openDetail(r.id)}
                >
                  <TableCell>{formatDt(r.mix_due_at)}</TableCell>
                  <TableCell
                    title={r.order_id != null ? `Внутрішній id у системі: ${r.order_id}` : undefined}
                  >
                    {displayOrderRef(r)}
                  </TableCell>
                  <TableCell>{r.product_name}</TableCell>
                  <TableCell>{r.machine_identifier}</TableCell>
                  <TableCell align="right">{r.order_quantity}</TableCell>
                  <TableCell>{formatDt(r.order_planned_start)}</TableCell>
                  <TableCell>
                    {r.lines_progress?.fully_added ?? 0} / {r.lines_progress?.total ?? 0}
                  </TableCell>
                  <TableCell align="right">
                    {r.mixing_scale != null ? `${Math.round(r.mixing_scale * 1000) / 10} %` : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={Boolean(detail)} onClose={closeDetail} fullScreen>
        <DialogTitle sx={{ pr: 1 }}>
          <PageBackHeader
            variant="dialog"
            title={`Змішування — замовлення ${detail ? displayOrderRef(detail) : ''} · ${detail?.product_name || ''}`}
            onBack={closeDetail}
          />
        </DialogTitle>
        <DialogContent>
          {detailLoading && (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={28} />
            </Box>
          )}
          {!detailLoading && detail && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" gutterBottom>
                Звести суміш до: <strong>{formatDt(detail.mix_due_at)}</strong> · Старт на ТПА:{' '}
                <strong>{formatDt(detail.order_planned_start)}</strong> · Статус: {detail.status_display}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                «Доступно (кг)» — залишок на складі виробництва (після переміщення зі складів сировини/барвників).
                «Частка за складом» — скільки від повної рецептури можна змішати зараз з цих залишків.
                «Зараз можна (кг)» — ціль по рядку з урахуванням цієї частки. Якщо в «Додати (кг)» ввести більше,
                ніж «Залишилось» до номіналу рецепту, нижче з’явиться підказка: скільки інших компонентів додати,
                щоб зберегти пропорцію техкарти. Списання при додаванні йде лише зі складу виробництва.
              </Typography>
              {displayRecipeHints && detail.status === 'pending' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" component="div" gutterBottom>
                    Масштаб суміші відносно рецепту: ×{displayRecipeHints.f}
                  </Typography>
                  <Typography variant="body2" component="div">
                    Щоб зберегти пропорції, загалом потрібно так (кг до цілі після цього кроку):
                  </Typography>
                  <Box component="ul" sx={{ m: '8px 0 0', pl: 2.5 }}>
                    {displayRecipeHints.rows
                      .filter((r) => r.need > EPS)
                      .map((r) => (
                        <Typography component="li" variant="body2" key={r.id}>
                          <strong>{r.label}</strong>: додати ще <strong>{r.needDisp}</strong> кг (ціль за
                          масштабом <strong>{r.targetDisp}</strong> кг загалом)
                        </Typography>
                      ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Якщо одночасно введено перевищення в кількох рядках, застосовується найбільший
                    коефіцієнт масштабу.
                  </Typography>
                </Alert>
              )}
              {detail.lines?.length ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Компонент</TableCell>
                      <TableCell align="right">Потрібно (кг)</TableCell>
                      <TableCell align="right">Вже додано</TableCell>
                      <TableCell align="right">Залишилось</TableCell>
                      <TableCell align="right">Доступно на СВ (кг)</TableCell>
                      <TableCell align="right">Зараз можна (кг)</TableCell>
                      <TableCell>Сушіння</TableCell>
                      <TableCell>Додати (кг)</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detail.lines || []).map((ln) => (
                      <TableRow key={ln.id}>
                        <TableCell>{ln.material_label}</TableCell>
                        <TableCell align="right">{ln.quantity_required}</TableCell>
                        <TableCell align="right">{ln.quantity_added}</TableCell>
                        <TableCell align="right">{ln.quantity_remaining}</TableCell>
                        <TableCell align="right">{ln.available_effective_kg}</TableCell>
                        <TableCell align="right">{ln.quantity_mixable_now_kg}</TableCell>
                        <TableCell>
                          {ln.needs_drying
                            ? `${ln.drying_time_minutes ?? '—'} хв`
                            : '—'}
                        </TableCell>
                        <TableCell sx={{ minWidth: 110 }}>
                          <TextField
                            size="small"
                            type="number"
                            inputProps={{ min: 0, step: 'any' }}
                            value={addQty[ln.id] ?? ''}
                            onChange={(e) =>
                              setAddQty((prev) => ({ ...prev, [ln.id]: e.target.value }))
                            }
                            disabled={detail.status !== 'pending'}
                          />
                          {(() => {
                            // Show proportional hint based on another line's entered value
                            const activeEntry = Object.entries(addQty).find(
                              ([id, val]) => id !== String(ln.id) && val !== '' && !isNaN(parseFloat(val)) && parseFloat(val) > 0
                            );
                            if (!activeEntry) return null;
                            const [activeId, activeVal] = activeEntry;
                            const activeLine = (detail.lines || []).find(l => String(l.id) === activeId);
                            if (!activeLine || !activeLine.quantity_required) return null;
                            const ratio = (parseFloat(activeVal) + (activeLine.quantity_added || 0)) / activeLine.quantity_required;
                            const hint = ratio * ln.quantity_required - (ln.quantity_added || 0);
                            if (hint <= 0) return null;
                            return (
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                за пропорцією: {hint.toFixed(2)} кг
                              </Typography>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={detail.status !== 'pending' || savingLine === ln.id}
                            onClick={() => submitAdd(ln.id)}
                          >
                            OK
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              ) : (
                <Typography color="text.secondary">Немає рядків матеріалів.</Typography>
              )}

              <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                Повна історія замісу
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                У хронологічному порядку: кожне натискання «OK» з додаванням кг. Для задач, створених до
                оновлення системи, журнал може бути порожнім — тоді видно лише підсумок у колонці «Вже
                додано».
              </Typography>
              {detail.mixing_add_history?.length ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell align="right" width={48}>
                          №
                        </TableCell>
                        <TableCell>Дата й час</TableCell>
                        <TableCell>Компонент</TableCell>
                        <TableCell align="right">Додано (кг)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.mixing_add_history.map((ev) => (
                        <TableRow key={ev.id}>
                          <TableCell align="right">{ev.sequence}</TableCell>
                          <TableCell>{formatDt(ev.created_at)}</TableCell>
                          <TableCell>{ev.material_label}</TableCell>
                          <TableCell align="right">{ev.quantity}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Записів додавань немає.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetail}>Закрити</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
